import React, { useState } from "react";
import { X, Lock, Save, User, GraduationCap as SchoolIcon, Flag } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { NATIONALITIES } from "@/lib/nationalities";
import AvatarBuilder from "@/components/AvatarBuilder";
import { AvatarPreview } from "@/lib/avatarBuilder";

/**
 * Self-service profile editor modal for the elim_user.
 * Tabs: Avatar | Perfil | Password.
 */
export default function EliminatoriasProfileDialog({ user, onClose, onSaved }) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4" data-testid="elim-profile-dialog">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-violet-600 to-violet-700 text-white">
          <h2 className="font-[Fredoka] text-xl font-bold">{t("elim_profile_title")}</h2>
          <button onClick={onClose} className="hover:bg-white/15 rounded-full p-1.5" data-testid="elim-profile-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <Tabs defaultValue="avatar" className="p-6">
          <TabsList className="grid grid-cols-3 w-full bg-slate-100 rounded-full p-1 mb-6">
            <TabsTrigger value="avatar" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow" data-testid="elim-profile-tab-avatar">{t("elim_profile_tab_avatar")}</TabsTrigger>
            <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow" data-testid="elim-profile-tab-profile">{t("elim_profile_tab_profile")}</TabsTrigger>
            <TabsTrigger value="password" className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow" data-testid="elim-profile-tab-password">{t("elim_profile_tab_password")}</TabsTrigger>
          </TabsList>
          <TabsContent value="avatar">
            <AvatarTab user={user} onSaved={onSaved} />
          </TabsContent>
          <TabsContent value="profile">
            <ProfileTab user={user} onSaved={onSaved} />
          </TabsContent>
          <TabsContent value="password">
            <PasswordTab onSuccess={onClose} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function AvatarTab({ user, onSaved }) {
  const { t } = useI18n();
  const [showBuilder, setShowBuilder] = useState(false);
  const [cfg, setCfg] = useState(user?.avatar_cfg || null);
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/eliminatoria/me", { avatar_cfg: next });
      setCfg(next);
      onSaved(data);
      toast.success(t("elim_profile_avatar_updated"));
    } catch (err) {
      toast.error(t("elim_profile_avatar_save_fail"));
    } finally {
      setSaving(false);
      setShowBuilder(false);
    }
  };

  return (
    <div className="text-center" data-testid="elim-profile-avatar-tab">
      <div className="inline-block">
        <AvatarPreview cfg={cfg} size={180} />
      </div>
      <p className="text-sm text-slate-600 mt-4 mb-5">
        {t("elim_profile_avatar_hint")}
      </p>
      <Button
        onClick={() => setShowBuilder(true)}
        disabled={saving}
        className="bg-violet-600 hover:bg-violet-700 rounded-full font-bold px-6"
        data-testid="elim-profile-edit-avatar"
      >
        {cfg ? t("elim_profile_edit_avatar") : t("elim_profile_create_avatar")}
      </Button>
      {showBuilder && (
        <AvatarBuilder
          current={cfg || undefined}
          onSave={save}
          onClose={() => setShowBuilder(false)}
        />
      )}
    </div>
  );
}

function ProfileTab({ user, onSaved }) {
  const { t } = useI18n();
  const [name, setName] = useState(user?.name || "");
  const [school, setSchool] = useState(user?.school || "");
  const [nationality, setNationality] = useState(user?.nationality || "PT");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/auth/eliminatoria/me", {
        name: name.trim() || null,
        school: school.trim() || null,
        nationality: nationality || null,
      });
      toast.success(t("elim_profile_updated"));
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("elim_profile_save_fail"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" autoComplete="off">
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><User className="w-4 h-4 text-slate-500" /> {t("elim_profile_name_label")}</Label>
        <Input data-testid="elim-profile-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("elim_profile_name_ph")} />
      </div>
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><SchoolIcon className="w-4 h-4 text-slate-500" /> {t("elim_profile_school_label")}</Label>
        <Input data-testid="elim-profile-school" value={school} onChange={(e) => setSchool(e.target.value)} placeholder={t("elim_profile_school_ph")} />
      </div>
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><Flag className="w-4 h-4 text-slate-500" /> {t("elim_profile_nationality_label")}</Label>
        <Select value={nationality} onValueChange={setNationality}>
          <SelectTrigger data-testid="elim-profile-nationality"><SelectValue /></SelectTrigger>
          <SelectContent>
            {NATIONALITIES.map((n) => (
              <SelectItem key={n.code} value={n.code}>{n.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button data-testid="elim-profile-save" type="submit" disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full font-bold h-11">
        <Save className="w-4 h-4 mr-2" /> {saving ? t("elim_profile_saving") : t("elim_profile_save_btn")}
      </Button>
    </form>
  );
}

function PasswordTab({ onSuccess }) {
  const { t } = useI18n();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error(t("elim_profile_pwd_min")); return; }
    if (next !== confirm) { toast.error(t("elim_profile_pwd_mismatch")); return; }
    setSaving(true);
    try {
      await api.post("/auth/eliminatoria/change-password", { current_password: current, new_password: next });
      toast.success(t("elim_profile_pwd_updated"));
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("elim_profile_pwd_update_fail"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" autoComplete="off">
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-slate-500" /> {t("elim_profile_cur_pwd_label")}</Label>
        <Input data-testid="elim-profile-cur-pwd" type="password" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
      </div>
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-slate-500" /> {t("elim_profile_new_pwd_label")}</Label>
        <Input data-testid="elim-profile-new-pwd" type="password" value={next} onChange={(e) => setNext(e.target.value)} placeholder={t("elim_profile_new_pwd_ph")} autoComplete="new-password" />
      </div>
      <div>
        <Label className="flex items-center gap-2 mb-1.5"><Lock className="w-4 h-4 text-slate-500" /> {t("elim_profile_confirm_label")}</Label>
        <Input data-testid="elim-profile-confirm-pwd" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t("elim_profile_confirm_ph")} autoComplete="new-password" />
      </div>
      <Button data-testid="elim-profile-pwd-save" type="submit" disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700 rounded-full font-bold h-11">
        {saving ? t("elim_profile_pwd_saving") : t("elim_profile_pwd_save_btn")}
      </Button>
    </form>
  );
}
